/**
 * /app/gateways — Tier-1 gateway management (the unified "Gateways" surface).
 *
 * The Tier-1 lens on the plant's data sources: every Thing (appliance
 * gateways, sensor children, standalone/virtual sources) and the tags it
 * publishes. "Furnace 1 PLC · Modbus-TCP · 24 tags · 12.4 msg/s · online ·
 * last seen 4 s ago." Industry-agnostic — same UI for any tenant.
 *
 * P2.2 vocabulary rename: this used to be the "Devices" page over
 * `/v1/iot/gateways`; it is now the **Things** page over `/v1/things` (the
 * unified surface from migration 0033). The tag count is rolled up per
 * thing: a single-device gateway merged its device into the kind='gateway'
 * Thing, so its tags sit directly on the gateway row, while multi-sensor
 * gateways carry tags on their children — we count BOTH (the thing's own
 * tags plus its children's) so the column is never misleadingly zero.
 *
 * Each row bound to a machine links to it on the Equipment page ("Open on the Equipment page").
 *
 * Live (2026-10-30): the list reads again every 30 s while the page is open, and every gateway's reading rate (with
 * its last 20 minutes) comes from one read for the org every 15 s (GET /v1/things/rates; the list itself has no rate
 * since roadmap perf #8). "Last seen" is the newest of the gateway's last_seen and its newest reading, and its age
 * moves on by itself. Refresh reads it all now. An API without the rates shows the rate as unknown ("—").
 *
 * A plant whose data all comes from files and that has no gateway at all sees "No gateways" and "This plant's data
 * comes from files." in place of an empty table (the 2026-10-24 page sweep, G3). Any gateway keeps the list, one not yet
 * connected among them, and staff always see the list (the #1641 review, I2).
 *
 * See docs/06_iot_twin_alignment.md § Devices vs Equipment.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { formatDistanceToNow } from "date-fns";
import {
  ArrowRight,
  Cpu,
  Filter,
  PackagePlus,
  Plus,
  Radar,
  Search,
  Trash2,
} from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { EmptyState } from "@/lib/ui/empty-state";
import { gatewaysFromFilesOnly, isFileImportSource } from "@/lib/gateway-list";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Input } from "@/lib/ui/input";
import { cn } from "@/lib/ui/cn";
import {
  SparklineCell,
  StatusPill,
  Table,
  type ColumnDef,
} from "@/lib/widgets";
import {
  things,
  listOrgTags,
  isLiveApi,
  backendErrorMessage,
  bulkDelete,
  bulkDeleteToast,
  useThingRatesQuery,
  useThingsQuery,
  type ThingProduct,
} from "@/lib/api";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/lib/ui/tooltip";
import { formatRate, newestTime, rateByThing, rateSparkline, useNow } from "@/lib/iot/gateway-live";
import { RefreshButton } from "@/components/iot/RefreshButton";
import { factoryApi } from "@/lib/api/platform-admin";
import { ConfirmDialog } from "@/lib/ui/confirm-dialog";
import {
  getProtocolLabel,
  useGateways,
  type GatewayRecord,
} from "@/lib/iot/gateways-store";
import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { AddGatewayDialog } from "@/components/iot/AddGatewayDialog";
import { ClaimSourceDialog } from "@/components/iot/ClaimSourceDialog";
import { GatewayDetailSheet } from "@/components/iot/GatewayDetailSheet";
import { usePageCopilotContext } from "@/lib/copilot/page-context";

type FilterKey = "all" | "online" | "warning" | "offline";

/** A table row: the gateway with its live rate laid over it. */
type GatewayRow = GatewayRecord & {
  /** The rate is known (the rates were read, or the page runs on fixtures); "—" otherwise. */
  rateKnown: boolean;
  /** Readings in the last 60 s, when the rates were read. */
  readingsLastMinute?: number;
};


const FILTER_DEFS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "All" },
  { key: "online", label: "Online" },
  { key: "warning", label: "Warning" },
  { key: "offline", label: "Offline" },
];

/**
 * Live-mode tag-count rollup. Fetches the org's things + tag catalogue and
 * returns a `Map<thingId, count>` where each thing's count is its OWN tags
 * plus the tags of its children (parent_thing_id === thing.id). This fixes
 * the merged-gateway count: a single-device gateway holds its tags on the
 * gateway thing itself, while a multi-sensor gateway holds them on children
 * — both roll up to the row the user sees. Disabled (empty map) in stub mode.
 */
function useThingTagCounts(): Map<string, number> {
  const enabled = isLiveApi;
  // The page's own read of every Thing (one cache entry, ["things"]): a second read of the same list only for the
  // rollup cost a second walk of every page.
  const thingsQuery = useThingsQuery();
  const tagsQuery = useQuery({
    queryKey: ["org-tags", "for-tag-rollup"],
    queryFn: () => listOrgTags(),
    enabled,
    staleTime: 30_000,
  });

  return useMemo(() => {
    const counts = new Map<string, number>();
    const items = thingsQuery.data?.items;
    const tags = tagsQuery.data?.items;
    if (!items || !tags) return counts;

    // Raw tag count keyed by the tag's owning id. NOTE: a tag's `device_id`
    // may reference EITHER the sensor-child Thing it lives on OR the parent
    // gateway (the merged-gateway case keeps tags on the gateway row) — so
    // we don't assume which, we just total per id and attribute below.
    const perOwner = new Map<string, number>();
    for (const t of tags) {
      perOwner.set(t.device_id, (perOwner.get(t.device_id) ?? 0) + 1);
    }

    // Children grouped by their parent gateway (gateway_id alias of
    // parent_thing_id), so a gateway row can roll up its children's tags.
    const childrenOf = new Map<string, string[]>();
    for (const thing of items) {
      if (thing.gateway_id) {
        const list = childrenOf.get(thing.gateway_id) ?? [];
        list.push(thing.id);
        childrenOf.set(thing.gateway_id, list);
      }
    }

    // Each Thing's count = its OWN tags + (for a gateway) its children's
    // tags. This fixes the bug where a sensor child with tags showed 0
    // because the tags were attributed to the id the API reports, while
    // the gateway over- or under-counted depending on that attribution.
    for (const thing of items) {
      let count = perOwner.get(thing.id) ?? 0;
      for (const childId of childrenOf.get(thing.id) ?? []) {
        count += perOwner.get(childId) ?? 0;
      }
      counts.set(thing.id, count);
    }
    return counts;
  }, [thingsQuery.data, tagsQuery.data]);
}

// useChildThingIds returns the ids of Things that are CHILD instruments (they
// carry a gateway_id / parent_thing_id). The gateways list excludes them — a
// VSync's instruments live under it (detail-sheet Instruments panel) and in the
// Equipment list, not as top-level gateways. Reuses the page's cached things query
// (["things"] → no extra fetch).
function useChildThingIds(): Set<string> {
  const thingsQuery = useThingsQuery();
  return useMemo(() => {
    const s = new Set<string>();
    for (const t of thingsQuery.data?.items ?? []) {
      if (t.gateway_id) s.add(t.id);
    }
    return s;
  }, [thingsQuery.data]);
}

/**
 * Live-mode product→field-protocol map. Fetches the gateway-product
 * catalogue and returns `Map<productId, fieldProtocolLabel>` so the Things
 * table can render the real field protocol for each Thing (P6.6) instead of
 * the retired hardcoded "modbus-tcp". Empty (disabled) in stub mode.
 */
function useProductProtocols(): Map<string, string> {
  const productsQuery = useQuery({
    queryKey: ["thing-products"],
    queryFn: () => things.products(),
    enabled: isLiveApi,
    staleTime: 5 * 60_000,
  });
  return useMemo(() => {
    const map = new Map<string, string>();
    const products: ThingProduct[] | undefined = productsQuery.data?.products;
    if (!products) return map;
    for (const p of products) {
      map.set(p.id, getProtocolLabel(p.field_protocol));
    }
    return map;
  }, [productsQuery.data]);
}

export function Devices() {
  const { gateways, remove: removeGatewayLocal } = useGateways();
  const { can, isPlatformAdmin, activeOrgId } = useOrg();
  // Things are data sources; writing them (add / claim / delete / bulk-delete)
  // is gateway commissioning → gate on gateway.commission so a TECHNICIAN can
  // onboard + manage hardware (matches the backend re-gate of the /v1/things +
  // /v1/gateways write routes). Members/admins keep it (the perm is in their
  // bundle); viewers/operators stay read-only.
  const canCommission = can("gateway.commission");
  const qc = useQueryClient();
  const toast = useToast();
  const [filter, setFilter] = useState<FilterKey>("all");
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [claimOpen, setClaimOpen] = useState(false);
  const [selectedGateway, setSelectedGateway] = useState<GatewayRecord | null>(null);
  // A link can open one gateway's sheet on a tab: ?gateway=<id>&tab=devices
  // (Go-live's "View on gateway", the wizard's breadcrumb).
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedGatewayId = searchParams.get("gateway");
  const linkedGateway = linkedGatewayId ? (gateways.find((g) => g.id === linkedGatewayId) ?? null) : null;
  const sheetGateway = selectedGateway ?? linkedGateway;
  const closeSheet = useCallback(() => {
    setSelectedGateway(null);
    if (searchParams.has("gateway") || searchParams.has("tab")) {
      const next = new URLSearchParams(searchParams);
      next.delete("gateway");
      next.delete("tab");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  // Demo (is_simulated) Things are hidden by default so the page leads with
  // real, tenant-added sources; a toggle reveals them. Live-mode only —
  // fixtures carry no is_simulated flag, so the filter is a no-op in stub mode.
  const [hideDemo, setHideDemo] = useState(true);
  const [purging, setPurging] = useState(false);
  // Multi-select for bulk delete (the shared Table renders the checkbox
  // column when `selectable`). Set of selected Thing ids; controlled here.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  usePageCopilotContext({ route: "/app/gateways", label: "Gateways" });

  // Per-row delete: confirm → DELETE /v1/things/{id} (204; cascades children
  // + tags in live mode) → optimistic local remove + refetch the things
  // query. Stub mode skips the network call and just removes locally.
  const handleDeleteThing = useCallback(
    async (thing: GatewayRecord) => {
      if (!window.confirm(`Delete "${thing.name}"? This removes the gateway and its tags.`)) {
        return;
      }
      if (isLiveApi) {
        setDeletingId(thing.id);
        try {
          await things.remove(thing.id);
        } catch (err) {
          toast.push("error", backendErrorMessage(err));
          return;
        } finally {
          setDeletingId(null);
        }
        void qc.invalidateQueries({ queryKey: ["things"] });
      }
      removeGatewayLocal(thing.id);
      toast.push("ok", `Removed ${thing.name}`);
    },
    [qc, toast, removeGatewayLocal],
  );

  // Bulk delete the ticked Things: confirm → DELETE each via things.remove
  // (Promise.allSettled; the server cascades each Thing's children + tags) →
  // invalidate the things query + drop the deleted rows locally → summary
  // toast (X deleted, Y failed). Stub mode skips the network and just removes
  // locally. Reuses the same per-Thing delete the single-row action uses.
  const handleBulkDelete = useCallback(async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkDeleting(true);
    try {
      let succeededIds = ids;
      if (isLiveApi) {
        const result = await bulkDelete(ids, (id) => things.remove(id));
        succeededIds = result.succeeded;
        void qc.invalidateQueries({ queryKey: ["things"] });
        const { kind, message } = bulkDeleteToast(result, "gateway");
        toast.push(kind, message);
      } else {
        toast.push("ok", `${ids.length} gateway${ids.length === 1 ? "" : "s"} deleted`);
      }
      // Drop deleted rows from local state + clear them from the selection.
      const deleted = new Set(succeededIds);
      for (const id of succeededIds) removeGatewayLocal(id);
      setSelectedIds((prev) => new Set([...prev].filter((id) => !deleted.has(id))));
    } finally {
      setBulkDeleting(false);
      setBulkConfirmOpen(false);
    }
  }, [selectedIds, qc, toast, removeGatewayLocal]);

  // Roll up tag counts per thing (live mode). Override the mapper's default
  // so each row shows its real (merged-gateway-aware) count.
  const tagCounts = useThingTagCounts();
  // P6.6: resolve each Thing's displayed *field protocol* from its gateway
  // product via the catalogue (no more hardcoded "modbus-tcp"). Transport
  // is always MQTT (shown in the detail sheet). Map is empty in stub mode.
  const protocolByProduct = useProductProtocols();
  // Child instrument Things (a VSync's sensor children — those with a
  // gateway_id/parent) must NOT appear in this GATEWAYS list: a VSync is the
  // gateway that connects instruments to the cloud, and its instruments are
  // managed under the gateway (the detail sheet's Instruments panel) and shown
  // in the Equipment list + process graph — not as top-level gateways with their
  // own "add instrument" cascade.
  const childIds = useChildThingIds();
  // The list reads again every 30 s while this page is open (every 2 min past one page of 200; the same cache entry
  // the gateways store fills), and the rates every 15 s. `now` moves the ages on between reads.
  const listQ = useThingsQuery({ poll: true });
  const ratesQ = useThingRatesQuery();
  // While the last read of the rates failed, they are not known: React Query keeps the last good answer, and
  // showing it would look live.
  const ratesData = ratesQ.isError ? undefined : ratesQ.data;
  const rates = useMemo(() => rateByThing(ratesData), [ratesData]);
  const now = useNow(5_000);
  const [refreshing, setRefreshing] = useState(false);
  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      await Promise.all([
        qc.refetchQueries({ queryKey: ["things"], exact: true }),
        qc.refetchQueries({ queryKey: ["things", "rates"], exact: true }),
        qc.invalidateQueries({ queryKey: ["org-tags", "for-tag-rollup"] }),
      ]);
    } finally {
      setRefreshing(false);
    }
  }, [qc]);

  const rows = useMemo<GatewayRow[]>(() => {
    return gateways
      // An import's source Thing is no gateway (lib/gateway-list): its data shows under Data sources.
      .filter((g) => !childIds.has(g.id) && !isFileImportSource(g))
      .map((g) => {
      // P-wave3: the things list now carries a reliable `tag_count` per row
      // (mapped onto `g.tags` in toThingRecord), so trust it first — this
      // fixes the column showing "0". Fall back to the merged-gateway rollup
      // only when the row's own count is zero (handles tags attributed to a
      // sensor child rather than the gateway row).
      const rollup = tagCounts.get(g.id);
      const tags = g.tags > 0 ? g.tags : (rollup ?? g.tags);
      // Derive the field-protocol label from the product when we have one;
      // otherwise keep whatever the row already carries (fixtures/stub).
      const protocol =
        g.productId && protocolByProduct.get(g.productId)
          ? protocolByProduct.get(g.productId)!
          : g.protocol || (g.productId ?? "");
      // The live rate over the row. A gateway the rates leave out had no reading in the window: 0. Without the
      // rates (not read yet, or an API without them) the live list has no rate at all; fixtures keep theirs.
      if (!rates) return { ...g, tags, protocol, rateKnown: !isLiveApi };
      const rate = rates.get(g.id);
      return {
        ...g,
        tags,
        protocol,
        msgPerSec: rate?.msg_per_sec ?? 0,
        msgPerSecHistory: rateSparkline(ratesData, g.id),
        lastSeen: newestTime(g.lastSeen, rate?.last_reading_at) ?? g.lastSeen,
        rateKnown: true,
        readingsLastMinute: rate?.last_60s ?? 0,
      };
    });
  }, [gateways, childIds, tagCounts, protocolByProduct, rates, ratesData]);

  const filtered = useMemo(() => {
    let result = rows;
    if (hideDemo) {
      result = result.filter((r) => !r.isSimulated);
    }
    if (filter !== "all") {
      result = result.filter((r) => r.status === filter);
    }
    if (search) {
      const needle = search.toLowerCase();
      result = result.filter(
        (r) =>
          r.name.toLowerCase().includes(needle) ||
          r.site.toLowerCase().includes(needle) ||
          r.protocol.toLowerCase().includes(needle),
      );
    }
    return result;
  }, [rows, filter, search, hideDemo]);

  // Keep the selection honest: drop ids that no longer exist (after a delete,
  // an org switch, or a server re-hydrate) so "Delete selected (N)" can't
  // count or act on ghost rows.
  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const live = new Set(rows.map((r) => r.id));
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (live.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [rows]);

  // No gateway at all, at a plant whose data comes from files (an import's source Thing is there): the page says so
  // in place of an empty table. Staff always see the list.
  const noGateways = useMemo(
    () => isLiveApi && !isPlatformAdmin && gatewaysFromFilesOnly(gateways, rows),
    [gateways, rows, isPlatformAdmin],
  );

  const counts = useMemo(
    () => ({
      all: rows.length,
      online: rows.filter((d) => d.status === "online").length,
      warning: rows.filter((d) => d.status === "warning").length,
      offline: rows.filter((d) => d.status === "offline").length,
    }),
    [rows],
  );

  // Demo (factory/simulated) Things present in this org — drives the
  // "Show/Hide demo" toggle visibility + the super-admin purge button.
  const demoCount = useMemo(() => rows.filter((r) => r.isSimulated).length, [rows]);

  // Super-admin bulk purge: delete ALL is_simulated Things (+ readings, tags,
  // bindings) for the active org. Real tenant-added Things are untouched.
  const handlePurgeDemo = useCallback(async () => {
    if (!activeOrgId) return;
    if (
      !window.confirm(
        `Permanently delete the ${demoCount} sample ${demoCount === 1 ? "gateway" : "gateways"} of this org and their data? Real gateways are not affected.`,
      )
    ) {
      return;
    }
    setPurging(true);
    try {
      const res = await factoryApi.purgeOrgSimulated(activeOrgId);
      toast.push("ok", `Deleted ${res.purged} sample ${res.purged === 1 ? "gateway" : "gateways"}`);
      void qc.invalidateQueries({ queryKey: ["things"] });
    } catch (err) {
      toast.push("error", backendErrorMessage(err));
    } finally {
      setPurging(false);
    }
  }, [activeOrgId, demoCount, toast, qc]);

  const columns = useMemo<ColumnDef<GatewayRow>[]>(
    () => [
      {
        id: "name",
        header: "Gateway",
        accessor: (d) => d.name,
        cell: (d) => (
          <div className="flex flex-col">
            <div className="flex items-center gap-1.5">
              <span className="font-medium text-on-surface">{d.name}</span>
              {d.isSimulated && (
                <Badge
                  variant="secondary"
                  className="text-[9px] uppercase tracking-wide"
                  title="Sample data, not a real source"
                >
                  Demo
                </Badge>
              )}
              {d.vendorName && (
                <Badge
                  variant="info"
                  className="text-[9px] uppercase tracking-wide"
                  title={`Supplied / monitored by ${d.vendorName}`}
                >
                  {d.vendorName}
                </Badge>
              )}
            </div>
            <span className="text-[11px] text-on-surface-muted">{d.site}</span>
          </div>
        ),
      },
      {
        id: "protocol",
        header: "Protocol",
        accessor: (d) => d.protocol,
        cell: (d) => (
          <Badge variant="secondary" className="font-mono uppercase tracking-wide">
            {d.protocol}
          </Badge>
        ),
      },
      {
        id: "tags",
        header: "Tags",
        accessor: (d) => d.tags,
        align: "right",
        cell: (d) => <span className="font-mono">{d.tags}</span>,
      },
      {
        id: "throughput",
        // Readings, not MQTT messages: one message of a poll carries many readings.
        header: "Readings a second",
        accessor: (d) => (d.rateKnown ? d.msgPerSec : -1),
        cell: (d) =>
          d.rateKnown ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-3">
                  <span className="w-12 text-right font-mono text-on-surface-muted">{formatRate(d.msgPerSec)}</span>
                  <div className="w-28">
                    <SparklineCell
                      points={d.msgPerSecHistory}
                      height={24}
                      ariaLabel={`${d.name} reading rate, last 20 minutes`}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-[18rem]">
                {d.readingsLastMinute !== undefined && (
                  <span className="block font-medium">
                    {d.readingsLastMinute} {d.readingsLastMinute === 1 ? "reading" : "readings"} in the last minute
                  </span>
                )}
                <span className="block text-on-surface-muted">
                  From this gateway and its instruments, a second on average. The line shows the last 20 minutes.
                </span>
              </TooltipContent>
            </Tooltip>
          ) : (
            <span className="inline-block w-12 text-right font-mono text-on-surface-muted">
              <span aria-hidden>—</span>
              <span className="sr-only">Rate not known</span>
            </span>
          ),
      },
      {
        id: "lastSeen",
        header: "Last seen",
        accessor: (d) => d.lastSeen,
        cell: (d) => (
          <span
            className={cn(
              "text-on-surface-muted",
              d.status === "offline" && "text-danger",
            )}
            title={absoluteTime(d.lastSeen)}
          >
            {formatRelative(d.lastSeen)}
          </span>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessor: (d) => d.status,
        cell: (d) => <StatusPill status={d.status} />,
      },
      {
        id: "actions",
        header: "",
        cell: (d) => (
          <div className="flex items-center justify-end gap-1">
            {d.twinEntityId ? (
              <Button
                size="sm"
                variant="ghost"
                asChild
                onClick={(e) => e.stopPropagation()}
              >
                <Link to={`/app/twin/equipment?entity=${encodeURIComponent(d.twinEntityId)}`}>
                  Open on the Equipment page
                  <ArrowRight className="ml-1 size-3.5" />
                </Link>
              </Button>
            ) : null}
            {canCommission && (
              <Button
                size="icon"
                variant="ghost"
                aria-label={`Delete ${d.name}`}
                title="Delete gateway"
                disabled={deletingId === d.id}
                className="text-on-surface-muted hover:text-danger"
                onClick={(e) => {
                  e.stopPropagation();
                  void handleDeleteThing(d);
                }}
              >
                <Trash2 className="size-4" />
              </Button>
            )}
          </div>
        ),
      },
    ],
    [canCommission, deletingId, handleDeleteThing],
  );

  return (
    <PageContainer className="max-w-7xl">
      <PageHeader
        breadcrumb={
          <>
            <Link to="/app" className="hover:text-on-surface">
              App
            </Link>
            <span aria-hidden>/</span>
            <span className="text-on-surface">Gateways</span>
          </>
        }
        title="Gateways"
        description="The gateways that send this plant its live readings, with the sensors and tags behind each one."
        actions={
          <>
            {isLiveApi && !noGateways && (
              <RefreshButton
                // Its own read only: the page's own reads every 15-30 s would make it look busy and refuse clicks.
                busy={refreshing}
                onRefresh={() => void refresh()}
                updatedAt={listQ.dataUpdatedAt}
                now={now}
                ariaLabel="Refresh the gateways"
                hint="Read the gateways, their status and their reading rates again now. The page also reads them again by itself while it is open."
              />
            )}
            {/* Demoted to a quiet secondary affordance (consistent with the
                onboarding Step-4 reframe): "Add Thing" + Simulate is the
                primary path; claiming a pre-provisioned/real gateway by code
                is the advanced exception, not a co-equal CTA. */}
            {canCommission && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setClaimOpen(true)}
                title="Have a provisioned gateway (real hardware or a claim code)? Claim it here."
                className="text-on-surface-muted"
              >
                <PackagePlus className="mr-1.5 size-4" />
                Claim a gateway
              </Button>
            )}
            {isPlatformAdmin && demoCount > 0 && (
              <Button
                variant="outline"
                size="sm"
                disabled={purging}
                onClick={() => void handlePurgeDemo()}
                title="Staff: delete every sample gateway of this org"
                className="text-on-surface-muted hover:text-danger"
              >
                <Trash2 className="mr-1.5 size-4" />
                {purging ? "Purging…" : `Purge demo (${demoCount})`}
              </Button>
            )}
            {canCommission && selectedIds.size > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBulkConfirmOpen(true)}
                title="Delete the selected gateways"
                className="text-danger hover:bg-danger/10"
              >
                <Trash2 className="mr-1.5 size-4" />
                Delete selected ({selectedIds.size})
              </Button>
            )}
            {canCommission && (
              <Button asChild size="sm">
                <Link to="/app/connect">
                  <Radar className="mr-1.5 size-4" />
                  Connect a device
                </Link>
              </Button>
            )}
            {canCommission && (
              <Button variant="outline" size="sm" data-tour="add-thing-btn" onClick={() => setAddOpen(true)}>
                <Plus className="mr-1.5 size-4" />
                Add gateway
              </Button>
            )}
          </>
        }
      />

      <AddGatewayDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onCreated={() => void qc.invalidateQueries({ queryKey: ["things"] })}
      />
      <ClaimSourceDialog
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        onClaimed={() => void qc.invalidateQueries({ queryKey: ["things"] })}
      />

      {noGateways ? (
        <EmptyState
          icon={Cpu}
          title="No gateways"
          description="This plant's data comes from files. Its files are under Data sources."
          cta={
            <Button asChild size="sm" variant="outline">
              <Link to="/app/twin/data-sources">Open Data sources</Link>
            </Button>
          }
        />
      ) : (
        <>
          {/* Stat row */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatCard label="Total gateways" value={counts.all} icon={Cpu} />
            <StatCard label="Online" value={counts.online} tone="success" />
            <StatCard label="Warning" value={counts.warning} tone="warning" />
            <StatCard label="Offline" value={counts.offline} tone="danger" />
          </div>

          {/* Filters */}
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1.5">
              <Filter className="size-4 text-on-surface-muted" />
              <span className="text-xs font-medium uppercase tracking-wide text-on-surface-muted">
                Filter
              </span>
            </div>
            <div className="flex gap-1">
              {FILTER_DEFS.map((def) => (
                <button
                  key={def.key}
                  type="button"
                  onClick={() => setFilter(def.key)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                    filter === def.key
                      ? "bg-brand text-brand-foreground"
                      : "bg-surface-muted text-on-surface-muted hover:bg-surface-muted/70 hover:text-on-surface",
                  )}
                >
                  {def.label}
                  <span
                    className={cn(
                      "ml-1.5 rounded-full px-1.5 py-0.5 text-[10px] font-mono",
                      filter === def.key
                        ? "bg-brand-foreground/20"
                        : "bg-surface/60",
                    )}
                  >
                    {counts[def.key]}
                  </span>
                </button>
              ))}
            </div>
            {demoCount > 0 && (
              <button
                type="button"
                onClick={() => setHideDemo((v) => !v)}
                className={cn(
                  "rounded-full px-3 py-1 text-xs font-medium transition-colors",
                  hideDemo
                    ? "bg-surface-muted text-on-surface-muted hover:bg-surface-muted/70 hover:text-on-surface"
                    : "bg-brand text-brand-foreground",
                )}
                title={hideDemo ? "Show the sample gateways" : "Hide the sample gateways"}
              >
                {hideDemo ? "Show demo" : "Hide demo"}
                <span className="ml-1.5 rounded-full bg-surface/60 px-1.5 py-0.5 font-mono text-[10px]">
                  {demoCount}
                </span>
              </button>
            )}
            <div className="relative ml-auto w-full sm:w-72">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-muted" />
              <Input
                placeholder="Filter by name, site or protocol…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
          </div>

          <div data-tour="things-table">
            <Table
              getRowId={(d) => d.id}
              columns={columns}
              data={filtered}
              // The first read of the list: a skeleton, not "No gateways match this filter".
              state={isLiveApi && listQ.isLoading && rows.length === 0 ? { kind: "loading" } : undefined}
              hideSearch
              pageSize={20}
              emptyTitle="No gateways match this filter"
              emptyDescription="Try widening the filter, or claim a new source to onboard a site."
              onRowClick={(g) => setSelectedGateway(g)}
              selectable={canCommission}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
            />
          </div>
        </>
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        onClose={() => {
          if (!bulkDeleting) setBulkConfirmOpen(false);
        }}
        onConfirm={handleBulkDelete}
        title={`Delete ${selectedIds.size} gateway${selectedIds.size === 1 ? "" : "s"}?`}
        description="This removes the selected gateways and their tags. This can't be undone."
        confirmLabel={`Delete ${selectedIds.size}`}
        destructive
      />

      <GatewayDetailSheet
        gateway={sheetGateway}
        onClose={closeSheet}
        initialTab={selectedGateway ? null : searchParams.get("tab")}
        canCommission={canCommission}
      />
    </PageContainer>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: number;
  icon?: typeof Cpu;
  tone?: "default" | "success" | "warning" | "danger";
}) {
  const toneClass = {
    default: "text-on-surface",
    success: "text-success",
    warning: "text-warning",
    danger: "text-danger",
  }[tone];

  return (
    <div className="flex items-center justify-between rounded-lg border border-border bg-surface-elevated p-3">
      <div className="flex flex-col gap-0.5">
        <span className="text-xs uppercase tracking-wide text-on-surface-muted">
          {label}
        </span>
        <span className={cn("text-2xl font-semibold tracking-tight", toneClass)}>
          {value}
        </span>
      </div>
      {Icon && <Icon className={cn("size-5", toneClass)} />}
    </div>
  );
}

function formatRelative(iso: string): string {
  try {
    // A time a little ahead of this browser's clock (the server's clock runs ahead) is "less than a minute ago",
    // never "in 1 minute".
    return formatDistanceToNow(new Date(Math.min(Date.parse(iso), Date.now())), { addSuffix: true });
  } catch {
    return iso;
  }
}

/** A time in full, in the browser's zone, for a hint. */
function absoluteTime(iso: string): string {
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" }) : iso;
}
